import { LoadingOutlined, SearchOutlined } from "@ant-design/icons"
import { AutoComplete, type AutoCompleteProps } from "antd"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useDebouncedCallback } from "../../hooks/use-debounced-callback.ts"
import { autocompleteAddress } from "../../services/shared/address-autocomplete"

interface Address {
  street: string
  city: string
  state: string
  zip_code: string
  display: string
  place_id: string
  confidence: number
}

export interface SuggestionCityZip {
  zip_code: string
  city: string
  state: string
}

export interface SuggestionAddress {
  address: Address
  city_zips: SuggestionCityZip[]
}

const hasStreet = (item: SuggestionAddress) => !!item.address?.street?.trim()

interface AddressAutoCompleteProps {
  id?: string
  onSelect?: (suggestion: SuggestionAddress) => void
  onChange?: (value: string) => void
  value?: string | null
  disabled?: boolean
  placeholder?: string
}

export function AddressAutoComplete({
  id,
  onSelect,
  onChange,
  value,
  disabled,
  placeholder
}: AddressAutoCompleteProps) {
  const { t } = useTranslation()
  const [internalValue, setInternalValue] = useState("")
  const [options, setOptions] = useState<AutoCompleteProps["options"]>([])
  const [suggestions, setSuggestions] = useState<SuggestionAddress[]>([])
  const [loading, setLoading] = useState(false)

  const handleSearch = async (search: string) => {
    if (search.length < 2) {
      setOptions([])
      setSuggestions([])
      return
    }

    setLoading(true)

    try {
      const res = await autocompleteAddress({ q: search })
      if (!res?.ok) throw new Error("error")

      const data: SuggestionAddress[] = res?.data?.suggestions || []
      setSuggestions(data)

      const newOptions: AutoCompleteProps["options"] = data.map(
        (item, index) => ({
          value: String(index),
          label: (
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontWeight: 500 }}>{item.address.display}</span>
              <span style={{ fontSize: 12, color: "#888" }}>
                {hasStreet(item)
                  ? null
                  : `${t("addressAutocomplete.cityOnly")} · `}
                {item.city_zips.length} ZIP code(s)
              </span>
            </div>
          )
        })
      )

      setOptions(newOptions)
    } catch (e) {
      console.error(e)
      setOptions([])
      setSuggestions([])
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (index: string) => {
    const selected = suggestions[Number(index)]
    if (selected) {
      const text = selected.address.street || selected.address.display
      setInternalValue(text)
      onChange?.(text)
      onSelect?.(selected)
    }
  }

  const handleChange = (data: string) => {
    setInternalValue(data)
    onChange?.(data)
  }

  const debounced = useDebouncedCallback(
    (search: string) => handleSearch(search),
    700
  )

  return (
    <AutoComplete
      id={id}
      disabled={disabled}
      value={value === undefined ? internalValue : (value ?? "")}
      options={options}
      onSelect={handleSelect}
      onSearch={debounced}
      onChange={handleChange}
      placeholder={placeholder ?? "Search for address..."}
      notFoundContent={loading ? "Searching..." : "No suggestions found"}
      prefix={<SearchOutlined />}
      suffixIcon={loading ? <LoadingOutlined /> : null}
    />
  )
}
